import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, MessageSquare } from "lucide-react";
import { SmsTemplateCard } from "./SmsTemplateCard";
import { SmsTemplateForm } from "./SmsTemplateForm";
import { SmsTemplatePreview } from "./SmsTemplatePreview";
import { useSmsTemplates } from "@/hooks/useSmsTemplates";
import type { SmsTemplate } from "@/types/sms-queue";
import { toast } from "sonner";

export const SmsTemplateManager = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
  
  const { 
    templates, 
    isLoading, 
    createTemplate, 
    updateTemplate, 
    deleteTemplate, 
    setDefaultTemplate 
  } = useSmsTemplates();

  const handleCreate = async (templateData: Omit<SmsTemplate, "id" | "created_at" | "user_id">) => {
    try {
      await createTemplate.mutateAsync(templateData);
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error("Erro ao criar template:", error);
    }
  };

  const handleEdit = async (templateData: Omit<SmsTemplate, "id" | "created_at" | "user_id">) => {
    if (!selectedTemplate) return;
    
    try {
      await updateTemplate.mutateAsync({
        id: selectedTemplate.id,
        ...templateData
      });
      setIsEditModalOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error("Erro ao atualizar template:", error);
    }
  };

  const handleDelete = async (id: string) => {
    const template = templates.find(t => t.id === id);
    if (template?.is_default) {
      toast.error("Não é possível excluir o template padrão. Defina outro template como padrão primeiro.");
      return;
    }

    if (confirm("Tem certeza que deseja excluir este template?")) {
      try {
        await deleteTemplate.mutateAsync(id);
      } catch (error) {
        console.error("Erro ao excluir template:", error);
      }
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultTemplate.mutateAsync(id);
    } catch (error) {
      console.error("Erro ao definir template padrão:", error);
    }
  };

  const handleOpenEdit = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setIsEditModalOpen(true);
  };

  const handleOpenPreview = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewModalOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando templates...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Templates de SMS
            </CardTitle>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhum template encontrado</h3>
              <p className="text-muted-foreground mb-4">
                Crie seu primeiro template de SMS para começar a enviar notificações personalizadas.
              </p>
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <SmsTemplateCard
                  key={template.id}
                  template={template}
                  onSetDefault={handleSetDefault}
                  onEdit={handleOpenEdit}
                  onDelete={handleDelete}
                  onPreview={handleOpenPreview}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criação */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Criar Novo Template SMS</DialogTitle>
          </DialogHeader>
          <SmsTemplateForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateModalOpen(false)}
            isLoading={createTemplate.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Template SMS</DialogTitle>
          </DialogHeader>
          <SmsTemplateForm
            template={selectedTemplate}
            onSubmit={handleEdit}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedTemplate(null);
            }}
            isLoading={updateTemplate.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Preview */}
      <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preview do Template</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <SmsTemplatePreview
              template={selectedTemplate}
              onClose={() => {
                setIsPreviewModalOpen(false);
                setSelectedTemplate(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};